import React, { useEffect, useState } from "react";
import { IonPage, IonContent,IonInput, IonLabel, IonFooter, IonButton, IonSpinner, useIonAlert, IonHeader, IonToolbar, IonGrid, IonRow, IonCol } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { ebioroApi } from '../../../services/api/ebioro/ebioro';
import { useHistory } from "react-router-dom";
import './EmailPage.css';
import AppHeader from "../../../components/AppHeader/AppHeader";
import { useAppSelector } from "../../../services/redux/hook";
import { useDispatch } from "../../../services/redux/store";
import { setUser } from "../../../services/redux/slices/user/user";
import { Sep10ChallengeParams, UserInfo } from "../../../types/types";
import { Networks } from "@stellar/stellar-sdk";
import { stellarApi } from "../../../services/api/stellar";
import { receivedSep10Token } from "../../../services/redux/slices/auth/sep10";
import * as Sentry from "@sentry/react";
import validator from 'validator';
import { Preferences } from '@capacitor/preferences';

const ebioroAuthDomain = import.meta.env.VITE_API_AUTH_DOMAIN || '';
const isTestnet = import.meta.env.VITE_isTestnet === 'true' ? true : false;

const EmailPage: React.FC<any> = () => {

    const { t, i18n } = useTranslation();
    const user:UserInfo = useAppSelector((state) => state.user);
    const walletInfo = useAppSelector((state) => state.walletInfo);
    const language = useAppSelector((state) => state.language);
    const termsURL = import.meta.env.VITE_EBIORO_TC_URL;
    const privacyURL = import.meta.env.VITE_EBIORO_PRIVACY_URL;
    const [isSpinner, setIsSpinner] = useState(false);
    const history = useHistory();
    const [present] = useIonAlert();
    const dispatch = useDispatch();
    const [emailAddress, setEmailAddress] = useState<any>('');
    const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);

    const presentErrorAlert = (error: any) => {

        present({
            header: 'Error',
            message: `${error.data}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        return
                    }
                }
            ],

        })

    }

    const sendOTP = async () => {

        setIsSpinner(true);

        const updatedUser = { ...user };

        const emailWithoutSpace = emailAddress.split(" ").join(""); 

        updatedUser.email = emailWithoutSpace;
        dispatch(setUser(updatedUser))

        let walletLanguage = language;

        try {

            const response = await dispatch(ebioroApi.endpoints.getEmailVerificationToken.initiate({ email: emailWithoutSpace, locale: walletLanguage }, { forceRefetch: true })).unwrap();

            history.push({
                pathname: '/signup/verify-email',
                state: {
                    detail: {
                        verifyTokenId: response.message,
                        auth_token: response.auth_token
                    }
                }
            }

            );
            setIsSpinner(false);

        } catch (err: any) {

            setIsSpinner(false);

            if (err.status == 409) {

                presentErrorAlert({ data: err.data.error })
                return

            }

            else {
                Sentry.captureException(err)
                presentErrorAlert({data:t('application.uncaught.ERROR')});
                return
            }

        }

    }

    useEffect(() => {

        const init = async () => {

            const params: Sep10ChallengeParams = {
                account: walletInfo.account_key as string,
                home_domain: ebioroAuthDomain,
                network: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
                privateKey: walletInfo.account_private_key as string
            }

            const { data } = await dispatch(stellarApi.endpoints.sep10.initiate(params));

            if (data) {

                dispatch(receivedSep10Token(data));

                // // this is needed until we finalize the migration from some api endpoint calls 
                // // to RTK query. 
                //await deviceStorage.set(ebioroAuthDomain, data[ebioroAuthDomain] as string);
                await Preferences.set({
                    key: ebioroAuthDomain,
                    value: JSON.stringify(data),
                });

            }

        }

        init()

    }, []);

    const checkEmailAddress = (email:string | null | undefined):boolean=>{

        if (email){

            const result = validator.isEmail(email);

            if (result){
                setEmailAddress(email);
                setIsButtonEnabled(true);
                // We are setting the user email here to know which user might face an error during registration.
                // After registration we will be setting the id of the user as well. 
                Sentry.setUser({
                    email:email
                });
            }

            else {
                setIsButtonEnabled(false)
            }

            return result

        }

        return false
    }

    return (
            <IonPage>

                <IonHeader className="ion-no-border">

                    <AppHeader
                        title={t('registration.email.HEADER') as string}
                        goBack={true}
                    >
                    </AppHeader>

                </IonHeader>

                <IonContent scrollY={false} className="ion-padding">

                    <IonGrid>
                        <IonRow>
                            <IonCol>
                            <IonInput
                                label={t('registration.email.EMAIL') as string}
                                className="ion-text-start"
                                labelPlacement="floating" 
                                mode="md"
                                fill="outline"
                                clearOnEdit={true}
                                placeholder={t('registration.email.EMAIL') as string} 
                                inputMode='email' 
                                onIonInput={(e:any) => checkEmailAddress(e.detail.value)}/>

                            </IonCol>
                        </IonRow>
                    </IonGrid>


                    <div className="TC">
                        <IonLabel >
                            <p >{t('registration.email.T&C-LABEL')}
                                <a target='_blank' href={`${termsURL}`}>{t('registration.email.T&C-LINK')}</a>
                                {t('registration.T&C-PP-SPACE')}
                                <a target='_blank' href={`${privacyURL}`}>{t('registration.PRIVACY-LINK')}</a>
                                {t('registration.email.T&C-REST')}

                            </p>

                        </IonLabel>
                    </div>

                </IonContent>

                <IonFooter className="ion-no-border">
                    <IonToolbar>
                    <IonButton
                        color='primary'
                        onClick={() => sendOTP()}
                        disabled={!isButtonEnabled}
                        type="submit"
                        expand="block"
                        id='pageFooterButton'>
                        {isSpinner ? (<IonSpinner className="BUTTON-SPINNER"></IonSpinner>)
                            : t('registration.email.BTN_CONT')}
                    </IonButton>
                    </IonToolbar>
                </IonFooter>

            </IonPage>
        
    )
}

export default EmailPage